// TurnaroundPicker — customer-facing radio-card picker for the per-product
// turnaround options the admin configures in the Product Edit dialog.
//
// Each option carries:
//   { key, label, business_days, price_mode: "percent"|"flat", price_value, description }
//
// A price_value of 0 surfaces as "Included" (no surcharge — the canonical
// "Standard" choice). Anything else renders as a tinted "+25%" or "+$45"
// badge inline with the label so the surcharge is visible BEFORE selection,
// not buried in the order summary.
import { Clock, Zap } from "lucide-react";
import { money } from "../lib/api";

export default function TurnaroundPicker({ options, selectedKey, onChange }) {
  if (!options?.length) return null;

  return (
    <div className="mt-5" data-testid="turnaround-picker">
      <div className="overline mb-2 flex items-center gap-2">
        <Clock className="w-3.5 h-3.5 text-kolt-orange" />
        Turnaround
      </div>
      <div className="space-y-2">
        {options.map((opt) => {
          const isSelected = selectedKey === opt.key;
          const value = Number(opt.price_value) || 0;
          const isRush = value > 0;
          const surchargeLabel = !isRush
            ? "Included"
            : opt.price_mode === "flat"
              ? `+${money(value)}`
              : `+${value}%`;
          return (
            <button
              key={opt.key}
              type="button"
              onClick={() => onChange(opt.key)}
              className={`w-full text-left border-2 transition-all p-3 flex items-center justify-between gap-3 ${
                isSelected
                  ? "border-kolt-orange bg-kolt-orange/5"
                  : "border-slate-200 hover:border-slate-400 bg-white"
              }`}
              data-testid={`turnaround-option-${opt.key}`}
              aria-pressed={isSelected}
            >
              <div className="flex items-start gap-3 min-w-0">
                <span
                  className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                    isSelected ? "border-kolt-orange bg-kolt-orange" : "border-slate-300"
                  }`}
                  aria-hidden
                />
                <div className="min-w-0">
                  <div className="font-bold text-sm text-kolt-slate-dark flex items-center gap-1.5">
                    {isRush && <Zap className="w-3.5 h-3.5 text-kolt-orange flex-shrink-0" />}
                    <span className="truncate">{opt.label}</span>
                  </div>
                  {opt.business_days && (
                    <div className="text-xs text-slate-500 font-mono uppercase tracking-wide mt-0.5">
                      {opt.business_days}
                    </div>
                  )}
                  {opt.description && (
                    <div className="text-xs text-slate-500 mt-1">{opt.description}</div>
                  )}
                </div>
              </div>
              <span
                className={`flex-shrink-0 font-mono text-[11px] tracking-[0.18em] uppercase font-bold py-1 px-2 ${
                  isRush
                    ? "bg-kolt-orange text-white"
                    : "bg-slate-100 text-slate-600"
                }`}
                data-testid={`turnaround-badge-${opt.key}`}
              >
                {surchargeLabel}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
